import { faChevronDown, faSort } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../../../components/base/Icon';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { useTranslation } from '../../../../providers/TranslationProvider';
import type {
	HeaderConfiguration,
	SortField,
	TableSortOption,
} from '../../../types';
import { useListBuilderContextSafe } from '../../core/ListBuilderProvider';

interface DropdownOption {
	value: string;
	label: string;
	description?: string;
}

interface SortDropdownProps {
	sortOptions: TableSortOption[];
	currentSortId?: string;
	defaultSortId?: string;
	onSortChange: (sortId: string) => void;
	disabled?: boolean;
	placeholder?: string;
}

export function SortDropdown({
	sortOptions,
	currentSortId,
	defaultSortId,
	onSortChange,
	disabled = false,
	placeholder = 'Default',
}: SortDropdownProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const listKey = useListBuilderContextSafe()?.listKey;

	// All hooks must be called unconditionally (before any early returns)
	const [isOpen, setIsOpen] = useState(false);
	const [dropdownPosition, setDropdownPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
	});
	const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom');
	const [focusedIndex, setFocusedIndex] = useState<number>(-1);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	// Find current sort option with proper fallback chain
	const currentSort = useMemo(() => {
		// If no options yet, return undefined (will render placeholder)
		if (sortOptions.length === 0) {
			return undefined;
		}

		// 1. Try exact ID match with currentSortId
		if (currentSortId != null) {
			const match = sortOptions.find((opt) => opt.id === currentSortId);

			if (match != null) {
				return match;
			}
		}

		// 2. Fallback to defaultSortId
		if (defaultSortId != null) {
			const match = sortOptions.find((opt) => opt.id === defaultSortId);

			if (match != null) {
				return match;
			}
		}

		// 3. Final fallback to first option
		return sortOptions[0];
	}, [sortOptions, currentSortId, defaultSortId]);

	// Convert sort options to dropdown options - memoized to prevent infinite loop
	const dropdownOptions: Array<DropdownOption> = useMemo(
		() =>
			sortOptions
				.map((option) => ({
					value: option.id,
					label: t(option.name),
					description:
						option.description != null ? t(option.description) : undefined,
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[sortOptions, t],
	);

	// Calculate dropdown position and placement
	useEffect(() => {
		if (isOpen && buttonRef.current != null) {
			const buttonRect = buttonRef.current.getBoundingClientRect();
			const windowHeight = window.innerHeight;
			const windowWidth = window.innerWidth;

			// Estimate dropdown height (max-h-60 = 15rem = 240px)
			// Each item is approximately 32px (py-1.5 = 12px + text/line-height ≈ 20px)
			const itemHeight = 32;
			const estimatedDropdownHeight = Math.min(
				240,
				dropdownOptions.length * itemHeight,
			);

			// Determine placement (above or below)
			const spaceBelow = windowHeight - buttonRect.bottom;
			const spaceAbove = buttonRect.top;

			let top = buttonRect.bottom - 1; // Changed from + 4 to - 1 to connect
			let actualPlacement: 'bottom' | 'top' = 'bottom';

			if (
				spaceBelow < estimatedDropdownHeight &&
				spaceAbove > estimatedDropdownHeight
			) {
				// Place above
				top = buttonRect.top - estimatedDropdownHeight + 1; // Changed from - 4 to + 1 to connect
				actualPlacement = 'top';
			}

			// Calculate left position to align with button
			let left = buttonRect.left;

			// Get the actual width needed for the dropdown content
			const maxContentWidth = Math.max(
				buttonRect.width,
				...dropdownOptions.map((opt) => {
					// Estimate text width (rough approximation)
					const textLength = (opt.label + (opt.description ?? '')).length;

					return Math.min(textLength * 8 + 48, 320); // padding + reasonable max
				}),
			);

			// Ensure dropdown doesn't go off-screen to the right
			if (left + maxContentWidth > windowWidth - 20) {
				left = Math.max(20, windowWidth - maxContentWidth - 20);
			}

			setDropdownPosition({ top, left, width: maxContentWidth });
			setPlacement(actualPlacement);
		}
	}, [isOpen, dropdownOptions]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent): void => {
			if (
				dropdownRef.current != null &&
				!dropdownRef.current.contains(event.target as Node) &&
				buttonRef.current != null &&
				!buttonRef.current.contains(event.target as Node) &&
				contentRef.current != null &&
				!contentRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			// Use 'click' instead of 'mousedown' to avoid interfering with button clicks
			document.addEventListener('click', handleClickOutside);

			return () => {
				document.removeEventListener('click', handleClickOutside);
			};
		}

		return undefined;
	}, [isOpen]);

	// Initialize focused index when dropdown opens
	useEffect(() => {
		if (isOpen) {
			const currentIndex = dropdownOptions.findIndex(
				(opt) => opt.value === currentSort?.id,
			);

			setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
		} else {
			setFocusedIndex(-1);
		}
	}, [isOpen, dropdownOptions, currentSort?.id]);

	// Auto-scroll focused option into view
	useEffect(() => {
		if (isOpen && focusedIndex >= 0 && contentRef.current != null) {
			const focusedElement = contentRef.current.querySelector(
				'[data-focused="true"]',
			);

			focusedElement?.scrollIntoView({ block: 'nearest' });
		}
	}, [isOpen, focusedIndex]);

	// Handle keyboard navigation
	useEffect(() => {
		if (isOpen) {
			const handleKeyDown = (event: KeyboardEvent): void => {
				switch (event.key) {
					case 'Escape':
						setIsOpen(false);
						break;
					case 'ArrowDown':
						event.preventDefault();
						setFocusedIndex((prev) =>
							prev < dropdownOptions.length - 1 ? prev + 1 : 0,
						);
						break;
					case 'ArrowUp':
						event.preventDefault();
						setFocusedIndex((prev) =>
							prev > 0 ? prev - 1 : dropdownOptions.length - 1,
						);
						break;
					case 'Enter':
						event.preventDefault();
						if (focusedIndex >= 0 && focusedIndex < dropdownOptions.length) {
							const focusedOption = dropdownOptions[focusedIndex];

							if (focusedOption != null) {
								handleSelect(focusedOption.value);
							}
						}
						break;
					case 'Home':
						event.preventDefault();
						setFocusedIndex(0);
						break;
					case 'End':
						event.preventDefault();
						setFocusedIndex(dropdownOptions.length - 1);
						break;
				}
			};

			document.addEventListener('keydown', handleKeyDown);

			return () => {
				document.removeEventListener('keydown', handleKeyDown);
			};
		}

		return undefined;
	}, [isOpen, focusedIndex, dropdownOptions]);

	const handleSelect = (optionId: string): void => {
		// Close dropdown first to prevent any interference
		setIsOpen(false);

		// Then trigger the sort change
		onSortChange(optionId);
	};

	// Early return after all hooks
	if (sortOptions.length === 0) {
		return null;
	}

	return (
		<div
			className="flex items-center"
			data-testid={listKey != null ? `${listKey}-sort-dropdown` : undefined}
		>
			<Icon
				icon={faSort}
				size="sm"
				color={colors.values.textMuted}
				className="mr-2"
			/>

			<div className="relative" ref={dropdownRef}>
				<button
					ref={buttonRef}
					type="button"
					onClick={() => {
						setIsOpen(!isOpen);
					}}
					disabled={disabled}
					className={`
            px-2 py-1 text-left text-xs whitespace-nowrap
            ${colors.classes.bgPrimary} 
            border ${isOpen ? 'border-blue-500 dark:border-blue-400' : colors.classes.borderDefault}
            ${isOpen ? 'rounded-t-md rounded-b-none' : 'rounded-md'} shadow-sm
            flex items-center justify-between gap-1.5
            ${
							disabled
								? 'opacity-50 cursor-not-allowed'
								: `${colors.classes.bgPrimaryHover} cursor-pointer`
						}
            ${isOpen ? 'ring-2 ring-blue-500/20 dark:ring-blue-400/20' : ''}
          `}
				>
					<span
						className={
							currentSort != null
								? colors.classes.textPrimary
								: colors.classes.textSecondary
						}
					>
						{currentSort != null ? t(currentSort.name) : t(placeholder)}
					</span>
					<Icon
						icon={faChevronDown}
						size="xs"
						color={colors.values.textMuted}
						className="ml-1"
					/>
				</button>

				{isOpen &&
					!disabled &&
					typeof document !== 'undefined' &&
					createPortal(
						<div
							ref={contentRef}
							onClick={(e) => {
								e.stopPropagation();
							}}
							className={`fixed z-[9999] ${colors.classes.bgPrimary} border border-blue-500 dark:border-blue-400 border-t-0 ${placement === 'bottom' ? 'rounded-b-md' : 'rounded-t-md'} shadow-lg max-h-60 overflow-auto`}
							style={{
								top: dropdownPosition.top,
								left: dropdownPosition.left,
								width: dropdownPosition.width,
								maxWidth: '320px',
							}}
						>
							{dropdownOptions.map((option, index) => {
								const isFocused = index === focusedIndex;

								return (
									<button
										key={option.value}
										type="button"
										onClick={() => {
											handleSelect(option.value);
										}}
										data-testid={
											listKey != null
												? `${listKey}-sort-option-${option.value}`
												: undefined
										}
										data-focused={isFocused ? 'true' : undefined}
										className={`
                  w-full px-2 py-1.5 text-left text-xs whitespace-nowrap cursor-pointer
                  hover:${colors.classes.bgSecondary} focus:${colors.classes.bgSecondary}
                  focus:outline-none active:${colors.classes.bgSecondary}
                  transition-colors duration-150
                  ${option.value === currentSort?.id || isFocused ? colors.classes.bgSecondary : ''}
                `}
									>
										<div
											className={`font-medium ${colors.classes.textPrimary}`}
										>
											{option.label}
										</div>
										{option.description && (
											<div
												className={`text-xs ${colors.classes.textSecondary} mt-0.5`}
											>
												{option.description}
											</div>
										)}
									</button>
								);
							})}
						</div>,
						document.body,
					)}
			</div>
		</div>
	);
}

// Helper function to determine sort indicators based on field type
function getSortIndicators(fieldName: string): { asc: string; desc: string } {
	const lowerFieldName = fieldName.toLowerCase();

	// Special handling for relevance
	if (fieldName === 'relevance') {
		// Only show one option for relevance
		return { asc: 'Most Relevant', desc: 'Most Relevant' };
	}

	// Date/time fields
	if (
		lowerFieldName.includes('date') ||
		lowerFieldName.includes('time') ||
		lowerFieldName.includes('created') ||
		lowerFieldName.includes('modified')
	) {
		return { asc: 'Oldest First', desc: 'Newest First' };
	}

	// Numeric fields (including IDs)
	if (
		lowerFieldName.includes('price') ||
		lowerFieldName.includes('amount') ||
		lowerFieldName.includes('total') ||
		lowerFieldName.includes('count') ||
		lowerFieldName.includes('quantity') ||
		lowerFieldName.includes('balance') ||
		lowerFieldName.includes('limit') ||
		lowerFieldName.includes('scripts') ||
		lowerFieldName.includes('trend') ||
		lowerFieldName.includes('diff') ||
		// `current_leads` / `max_leads` and similar are integers — without this
		// the sort dropdown labels them "A-Z" / "Z-A" which is misleading.
		lowerFieldName.includes('leads') ||
		lowerFieldName === 'id' ||
		lowerFieldName.includes('_id')
	) {
		return { asc: 'Low to High', desc: 'High to Low' };
	}

	// Boolean fields
	if (lowerFieldName.startsWith('is') || lowerFieldName.startsWith('has')) {
		return { asc: 'No First', desc: 'Yes First' };
	}

	// Default for text fields
	return { asc: 'A-Z', desc: 'Z-A' };
}

// Helper function to merge column sorts with table sorts
export function mergeSortOptions(
	tableSorts: TableSortOption[],
	columnSorts: Array<{
		columnId: string;
		columnLabel: string;
		headers: HeaderConfiguration[];
		sortFields: SortField[];
	}>,
	defaultSortId?: string,
): TableSortOption[] {
	const sortOptionsMap = new Map<string, TableSortOption>();

	// Step 1: Add all column sorts first (both original and flipped versions)
	// The suffix (-asc/-desc) indicates the first field's direction
	// Flipping inverts ALL field directions to get the true reverse
	columnSorts.forEach(({ columnId, columnLabel, headers, sortFields }) => {
		const primaryField = sortFields?.[0];

		if (sortFields != null && sortFields.length > 0 && primaryField != null) {
			// Use first header text as the label
			const headerText = headers?.[0]?.text ?? columnLabel ?? '';
			const firstFieldDirection = primaryField.direction ?? 'ASC';
			const indicators = getSortIndicators(primaryField.fieldName);

			// Add option matching first field's direction (original field directions)
			const originalIndicator =
				firstFieldDirection === 'DESC' ? indicators.desc : indicators.asc;
			const originalName = `${headerText} (${originalIndicator})`;

			if (!sortOptionsMap.has(originalName)) {
				sortOptionsMap.set(originalName, {
					id: `column-${columnId}-${firstFieldDirection.toLowerCase()}`,
					name: originalName,
					description: `Sort by ${headerText} in ${firstFieldDirection.toLowerCase()}ending order`,
					fields: sortFields, // Keep original directions
				});
			}

			// Add flipped option (all field directions inverted)
			const flippedDirection = firstFieldDirection === 'DESC' ? 'ASC' : 'DESC';
			const flippedIndicator =
				flippedDirection === 'DESC' ? indicators.desc : indicators.asc;
			const flippedName = `${headerText} (${flippedIndicator})`;

			if (!sortOptionsMap.has(flippedName)) {
				sortOptionsMap.set(flippedName, {
					id: `column-${columnId}-${flippedDirection.toLowerCase()}`,
					name: flippedName,
					description: `Sort by ${headerText} in ${flippedDirection.toLowerCase()}ending order`,
					fields: sortFields.map((field) => ({
						...field,
						direction: field.direction === 'ASC' ? 'DESC' : 'ASC',
					})),
				});
			}
		}
	});

	// Step 2: Add table sorts with both ASC and DESC options
	tableSorts.forEach((tableSort) => {
		// If this exact name already exists from a column sort, KEEP the column sort
		// Column sorts have IDs like `column-{columnId}-{direction}` that match header clicks
		if (sortOptionsMap.has(tableSort.name)) {
			return; // Skip this table sort - column sort already has the correct ID
		}

		// Special handling for relevance sort - add as-is without creating ASC/DESC versions
		if (tableSort.id === 'relevance') {
			sortOptionsMap.set(tableSort.name, tableSort);
			return;
		}

		// For table sorts, check if they already have ASC/DESC in the name
		const hasDirectionIndicator =
			/\((A-Z|Z-A|Low to High|High to Low|Newest First|Oldest First|Earliest First|Latest First|Yes First|No First|Most Relevant)\)/.test(
				tableSort.name,
			);

		const primaryField = tableSort.fields[0];

		if (
			hasDirectionIndicator ||
			tableSort.fields.length === 0 ||
			primaryField == null
		) {
			// If it already has a direction indicator or no fields, add as-is
			sortOptionsMap.set(tableSort.name, tableSort);
		} else {
			// Create both original and flipped versions
			// The suffix (-asc/-desc) indicates the first field's direction
			// Flipping inverts ALL field directions to get the true reverse
			const firstFieldDirection = primaryField.direction ?? 'ASC';
			const indicators = getSortIndicators(primaryField.fieldName);
			const baseName = tableSort.name;

			// Add option matching first field's direction (original field directions)
			const originalIndicator =
				firstFieldDirection === 'DESC' ? indicators.desc : indicators.asc;
			const originalName = `${baseName} (${originalIndicator})`;

			if (!sortOptionsMap.has(originalName)) {
				sortOptionsMap.set(originalName, {
					...tableSort,
					id: `${tableSort.id}-${firstFieldDirection.toLowerCase()}`,
					name: originalName,
					fields: tableSort.fields, // Keep original directions
				});
			}

			// Add flipped option (all field directions inverted)
			const flippedDirection = firstFieldDirection === 'DESC' ? 'ASC' : 'DESC';
			const flippedIndicator =
				flippedDirection === 'DESC' ? indicators.desc : indicators.asc;
			const flippedName = `${baseName} (${flippedIndicator})`;

			if (!sortOptionsMap.has(flippedName)) {
				sortOptionsMap.set(flippedName, {
					...tableSort,
					id: `${tableSort.id}-${flippedDirection.toLowerCase()}`,
					name: flippedName,
					fields: tableSort.fields.map((field) => ({
						...field,
						direction: field.direction === 'ASC' ? 'DESC' : 'ASC',
					})),
				});
			}
		}
	});

	return Array.from(sortOptionsMap.values()).sort((a, b) =>
		a.name.localeCompare(b.name),
	);
}
